import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Headset,
  Loader2,
  UserCheck,
} from "lucide-react";
import { useAdminAuth } from "../auth/AdminAuthContext";
import {
  supportEscalationsService,
  type SupportEscalationDetail,
  type SupportEscalationSummary,
} from "../../services/supportEscalationsService";
import { isApiEnabled } from "../../api/config";
import { ApiError } from "../../api/client";

/** Primary inbox chips — closed is separate; default = open activity. */
type InboxFilter =
  | "active"
  | "ai"
  | "exhibitor"
  | "visitor"
  | "escalated_open"
  | "escalated_done";

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function EventBadge({
  title,
  city,
}: {
  title?: string;
  city?: string | null;
}) {
  if (!title) return null;
  return (
    <span className="inline-flex items-center max-w-full text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-800 border border-violet-100 truncate">
      {title}
      {city ? ` · ${city}` : ""}
    </span>
  );
}

function filterToApi(filter: InboxFilter): {
  scope: "all" | "ai" | "escalated";
  status: "open" | "done" | "all";
  audience: "all" | "exhibitor" | "visitor";
} {
  switch (filter) {
    case "ai":
      return { scope: "ai", status: "open", audience: "all" };
    case "exhibitor":
      return { scope: "all", status: "open", audience: "exhibitor" };
    case "visitor":
      return { scope: "all", status: "open", audience: "visitor" };
    case "escalated_open":
      return { scope: "escalated", status: "open", audience: "all" };
    case "escalated_done":
      return { scope: "escalated", status: "done", audience: "all" };
    case "active":
    default:
      return { scope: "all", status: "open", audience: "all" };
  }
}

function formatPhone(phone: string | null | undefined): string {
  const p = (phone ?? "").trim();
  return p || "телефон в профиле не указан";
}

/**
 * Platform-wide Helper chats — all AI dialogs + escalations, paginated.
 */
export function AdminSupportInboxPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAdminAuth();
  const skipDeepLink = useRef(false);

  const [items, setItems] = useState<SupportEscalationSummary[]>([]);
  const [filter, setFilter] = useState<InboxFilter>("active");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<SupportEscalationDetail | null>(null);
  const [selectedEventMeta, setSelectedEventMeta] = useState<{
    eventId: string;
    eventTitle?: string;
    eventCity?: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const deepEvent = searchParams.get("event");
  const deepSession = searchParams.get("session");
  const pageSize = 20;

  const loadList = useCallback(async () => {
    if (!isApiEnabled()) {
      setItems([]);
      return;
    }
    const api = filterToApi(filter);
    const data = await supportEscalationsService.listChats({
      ...api,
      page,
      pageSize,
    });
    setItems(data.items);
    setTotal(data.total);
    setTotalPages(data.totalPages);
    if (data.page !== page) setPage(data.page);
  }, [filter, page]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      await loadList();
    } catch {
      setError("Не удалось загрузить диалоги");
    } finally {
      setLoading(false);
    }
  }, [loadList]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (selected) return;
    const t = window.setInterval(() => {
      void loadList().catch(() => undefined);
    }, 20_000);
    return () => window.clearInterval(t);
  }, [loadList, selected]);

  const backToList = useCallback(() => {
    skipDeepLink.current = true;
    setSelected(null);
    setSelectedEventMeta(null);
    setReplyText("");
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const openChat = useCallback(
    async (
      eventId: string,
      sessionId: string,
      meta?: { eventTitle?: string; eventCity?: string | null },
    ) => {
      setError("");
      skipDeepLink.current = false;
      try {
        let detail = await supportEscalationsService.get(eventId, sessionId);
        if (!detail) {
          setError("Диалог не найден");
          return;
        }
        if (
          detail.managerRequested &&
          detail.status === "open" &&
          !detail.claimedByUserId &&
          user?.id
        ) {
          try {
            detail = await supportEscalationsService.claim(eventId, sessionId, false);
          } catch (err) {
            if (!(err instanceof ApiError && err.status === 409)) throw err;
            detail =
              (await supportEscalationsService.get(eventId, sessionId)) ?? detail;
          }
        }
        setSelected({ ...detail, eventId });
        setSelectedEventMeta({
          eventId,
          eventTitle: meta?.eventTitle ?? detail.eventTitle,
          eventCity: meta?.eventCity ?? detail.eventCity,
        });
        setReplyText("");
        setSearchParams(
          { event: eventId, session: sessionId },
          { replace: true },
        );
      } catch {
        setError("Не удалось открыть диалог");
      }
    },
    [setSearchParams, user?.id],
  );

  useEffect(() => {
    if (skipDeepLink.current) {
      skipDeepLink.current = false;
      return;
    }
    if (!deepEvent || !deepSession) return;
    if (selected?.sessionId === deepSession) return;
    void openChat(deepEvent, deepSession);
  }, [deepEvent, deepSession, openChat, selected?.sessionId]);

  const changeFilter = (next: InboxFilter) => {
    backToList();
    setFilter(next);
    setPage(1);
  };

  const markDone = async (eventId: string, sessionId: string) => {
    await supportEscalationsService.setStatus(eventId, sessionId, "done");
    backToList();
    await loadList();
  };

  const takeEscalation = async (force: boolean) => {
    if (!selected?.eventId) return;
    setClaiming(true);
    try {
      const detail = await supportEscalationsService.claim(
        selected.eventId,
        selected.sessionId,
        force,
      );
      setSelected({ ...detail, eventId: selected.eventId });
      await loadList();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const detail = await supportEscalationsService.get(
          selected.eventId,
          selected.sessionId,
        );
        if (detail) setSelected({ ...detail, eventId: selected.eventId });
        setError("Диалог уже взял другой администратор");
      } else {
        setError("Не удалось взять диалог");
      }
    } finally {
      setClaiming(false);
    }
  };

  const releaseEscalation = async () => {
    if (!selected?.eventId) return;
    setClaiming(true);
    try {
      const detail = await supportEscalationsService.release(
        selected.eventId,
        selected.sessionId,
        false,
      );
      setSelected({ ...detail, eventId: selected.eventId });
      await loadList();
    } catch {
      setError("Не удалось освободить диалог");
    } finally {
      setClaiming(false);
    }
  };

  const sendReply = async () => {
    if (!selected?.eventId || !replyText.trim()) return;
    setReplying(true);
    try {
      const detail = await supportEscalationsService.reply(
        selected.eventId,
        selected.sessionId,
        replyText.trim(),
      );
      if (detail) {
        setSelected({ ...detail, eventId: selected.eventId });
        setReplyText("");
      }
      await loadList();
    } catch {
      setError("Не удалось отправить ответ");
    } finally {
      setReplying(false);
    }
  };

  const otherClaim =
    selected?.claimedByUserId && selected.claimedByUserId !== user?.id;
  const mineClaim =
    selected?.claimedByUserId && selected.claimedByUserId === user?.id;
  const isEscalated = selected?.managerRequested === true;

  return (
    <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0">
          <Headset size={20} />
        </div>
        <div className="min-w-0">
          <h1 className="text-[18px] text-gray-900 font-medium">Чат поддержки</h1>
          <p className="text-[12px] text-gray-500 mt-0.5">
            Все диалоги с Helper по выставкам — смотрите, где бот отвечает неверно.
          </p>
        </div>
      </div>

      <p className="text-[12px] text-gray-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
        По умолчанию — открытые диалоги (без закрытых). Смотрите AI у представителей, если
        бот отвечает неверно. Ответ менеджера уходит пользователю в тот же чат.
      </p>

      {error && <p className="text-[13px] text-red-600">{error}</p>}

      {!selected ? (
        <>
          <div className="flex gap-1.5 flex-wrap items-center">
            {(
              [
                ["active", "Активные"],
                ["ai", "AI"],
                ["exhibitor", "Представители"],
                ["visitor", "Посетители"],
                ["escalated_open", "К менеджеру"],
                ["escalated_done", "Закрытые"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => changeFilter(id)}
                className={`px-2.5 py-1 rounded-full text-[11px] border ${
                  filter === id
                    ? id === "escalated_done"
                      ? "bg-gray-100 border-gray-300 text-gray-700"
                      : "bg-indigo-100 border-indigo-200 text-indigo-700"
                    : "border-gray-200 text-gray-500"
                }`}
              >
                {label}
              </button>
            ))}
            <span className="ml-auto text-[11px] text-gray-400 tabular-nums">
              {total > 0 ? `${total}` : ""}
            </span>
          </div>

          {loading ? (
            <div className="flex justify-center py-10 text-gray-400">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => {
                const eventId = item.eventId;
                if (!eventId) return null;
                const takenByMe =
                  item.claimedByUserId && item.claimedByUserId === user?.id;
                const takenByOther =
                  item.claimedByUserId && item.claimedByUserId !== user?.id;
                return (
                  <button
                    key={`${eventId}-${item.sessionId}`}
                    type="button"
                    onClick={() =>
                      void openChat(eventId, item.sessionId, {
                        eventTitle: item.eventTitle,
                        eventCity: item.eventCity,
                      })
                    }
                    className={`w-full text-left bg-white rounded-xl border p-3.5 transition-colors ${
                      takenByOther
                        ? "border-sky-200 hover:border-sky-300"
                        : takenByMe
                          ? "border-emerald-200 hover:border-emerald-300"
                          : "border-gray-100 hover:border-indigo-200"
                    }`}
                  >
                    <div className="mb-1 flex items-center gap-1.5 flex-wrap">
                      <EventBadge title={item.eventTitle} city={item.eventCity} />
                      {item.managerRequested ? (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded ${
                            item.status === "open"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {item.status === "open" ? "к менеджеру" : "закрыто"}
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                          AI-чат
                        </span>
                      )}
                      {item.isExhibitor ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-800 border border-teal-100">
                          представитель
                        </span>
                      ) : null}
                      {takenByMe ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                          у вас
                        </span>
                      ) : takenByOther ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">
                          {item.claimedByName || "админ"}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-[14px] text-gray-900 truncate">
                        {item.userName}
                      </span>
                      <span className="text-[11px] text-gray-400 flex-shrink-0">
                        {item.messageCount != null ? `${item.messageCount} сообщ.` : ""}
                      </span>
                    </div>
                    {item.lastUserMessage ? (
                      <p className="text-[12px] text-indigo-800/80 mt-1 line-clamp-1">
                        Пользователь: {item.lastUserMessage}
                      </p>
                    ) : null}
                    {item.lastAssistantMessage ? (
                      <p className="text-[12px] text-gray-600 mt-0.5 line-clamp-2">
                        Бот: {item.lastAssistantMessage}
                      </p>
                    ) : (
                      <p className="text-[12px] text-gray-500 mt-0.5 line-clamp-2">
                        {item.pendingRequest || "—"}
                      </p>
                    )}
                    <p className="text-[11px] text-gray-400 mt-1">
                      {formatPhone(item.phone)} ·{" "}
                      {formatWhen(item.lastMessageAt || item.updatedAt)}
                    </p>
                  </button>
                );
              })}
              {items.length === 0 && (
                <p className="text-center text-[13px] text-gray-400 py-8">
                  {filter === "escalated_done"
                    ? "Нет закрытых запросов"
                    : filter === "exhibitor"
                      ? "Нет открытых диалогов представителей"
                      : "Нет открытых диалогов в этом фильтре"}
                </p>
              )}
            </div>
          )}

          {totalPages > 1 ? (
            <div className="flex items-center justify-center gap-2 pt-1">
              <button
                type="button"
                aria-label="Предыдущая страница"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-[12px] text-gray-500 tabular-nums min-w-[4.5rem] text-center">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                aria-label="Следующая страница"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="mb-1 flex gap-1.5 flex-wrap">
                <EventBadge
                  title={selectedEventMeta?.eventTitle}
                  city={selectedEventMeta?.eventCity}
                />
                {isEscalated ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                    к менеджеру
                  </span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    AI-чат
                  </span>
                )}
              </div>
              <p className="text-[15px] text-gray-900">{selected.userName}</p>
              <p className="text-[12px] text-gray-500">
                {formatPhone(selected.phone)} ·{" "}
                {formatWhen(
                  selected.messages[selected.messages.length - 1]?.createdAt ||
                    selected.escalatedAt ||
                    selected.updatedAt,
                )}
              </p>
              {selectedEventMeta?.eventId ? (
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/events/${selectedEventMeta.eventId}/support`)
                  }
                  className="text-[11px] text-indigo-600 hover:text-indigo-800 mt-1"
                >
                  Открыть поддержку выставки
                </button>
              ) : null}
            </div>
            <div className="flex gap-1 flex-wrap justify-end">
              {isEscalated && selected.status === "open" ? (
                <button
                  type="button"
                  onClick={() => void markDone(selected.eventId!, selected.sessionId)}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-[12px]"
                >
                  <CheckCircle2 size={12} /> Закрыть
                </button>
              ) : null}
              <button
                type="button"
                onClick={backToList}
                className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-[12px] text-gray-600"
              >
                К списку
              </button>
            </div>
          </div>

          {isEscalated ? (
            otherClaim ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2">
                <p className="text-[12px] text-sky-900 flex-1">
                  <UserCheck size={13} className="inline mr-1 -mt-0.5" />
                  Диалог взял {selected.claimedByName || "другой администратор"}
                  {selected.claimedAt ? ` · ${formatWhen(selected.claimedAt)}` : ""}
                </p>
                <button
                  type="button"
                  disabled={claiming}
                  onClick={() => void takeEscalation(true)}
                  className="px-2.5 py-1.5 rounded-lg bg-white border border-sky-200 text-sky-800 text-[12px] disabled:opacity-50"
                >
                  {claiming ? "…" : "Перехватить"}
                </button>
              </div>
            ) : mineClaim ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[12px] text-emerald-900 flex-1">
                  <UserCheck size={13} className="inline mr-1 -mt-0.5" />
                  Диалог у вас в работе
                </p>
                <button
                  type="button"
                  disabled={claiming}
                  onClick={() => void releaseEscalation()}
                  className="px-2.5 py-1.5 rounded-lg bg-white border border-emerald-200 text-emerald-800 text-[12px] disabled:opacity-50"
                >
                  Освободить
                </button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-[12px] text-amber-900 flex-1">Диалог свободен</p>
                <button
                  type="button"
                  disabled={claiming}
                  onClick={() => void takeEscalation(false)}
                  className="px-2.5 py-1.5 rounded-lg bg-amber-600 text-white text-[12px] disabled:opacity-50"
                >
                  {claiming ? "…" : "Взять в работу"}
                </button>
              </div>
            )
          ) : null}

          {selected.pendingRequest ? (
            <p className="text-[12px] text-amber-900 bg-amber-50 rounded-lg px-3 py-2">
              Запрос: {selected.pendingRequest}
            </p>
          ) : null}

          <div className="max-h-[50vh] overflow-y-auto space-y-2 border-t border-gray-50 pt-3">
            {selected.messages.length === 0 ? (
              <p className="text-[13px] text-gray-400 py-6 text-center">
                В этой сессии пока нет сообщений. Карточка могла подняться вверх после
                взятия в работу / закрытия — смотрите дату сообщений, не только «обновлено».
              </p>
            ) : (
              selected.messages.map((m) => {
                const isManager =
                  m.meta?.kind === "manager_reply" || m.meta?.fromAdmin === true;
                return (
                  <div
                    key={m.id}
                    className={`text-[13px] leading-snug rounded-lg px-3 py-2 ${
                      m.role === "user"
                        ? "bg-indigo-50 text-indigo-950 ml-6"
                        : isManager
                          ? "bg-emerald-50 text-emerald-950 mr-6"
                          : "bg-gray-50 text-gray-800 mr-6"
                    }`}
                  >
                    <p className="text-[10px] text-gray-400 mb-0.5">
                      {m.role === "user"
                        ? "Пользователь"
                        : isManager
                          ? "Менеджер"
                          : "Помощник"}{" "}
                      · {formatWhen(m.createdAt)}
                    </p>
                    <p className="whitespace-pre-wrap">{m.content}</p>
                  </div>
                );
              })
            )}
          </div>

          <div className="border-t border-gray-100 pt-3 space-y-2">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={3}
              placeholder="Ответ пользователю в чат помощника…"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] resize-y min-h-[72px]"
            />
            <button
              type="button"
              disabled={replying || !replyText.trim()}
              onClick={() => void sendReply()}
              className="w-full sm:w-auto px-4 py-2 rounded-lg bg-indigo-600 text-white text-[13px] disabled:opacity-50"
            >
              {replying ? "Отправка…" : "Отправить в чат"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
